import {useCallback, useRef} from 'react'
import {DiffIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import {ForYouItem} from '../shared/ForYouItem'
import {ForYouLeadingIcon} from '../shared/ForYouLeadingIcon'
import {useUnifiedBatchCount} from '../../../../stores/layout-selectors'
import {DockedPanel, useIsDockedPanelOpen} from '../../../../contexts/DockedPanelContext'
import {useOpenManageBatchPanel} from '../../../../hooks/use-open-manage-batch-panel'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'

const ITEM_NAME = 'pending-batch'

/**
 * Surfaces the viewer's unsubmitted batched changes so they can
 * recover that work and finish submitting it.
 */

export function PendingBatch() {
  const unifiedBatchEnabled = isFeatureEnabled('unified_batch_pr_comments')
  const batchCount = useUnifiedBatchCount()

  const anchorRef = useRef<HTMLButtonElement>(null)
  const isPanelOpen = useIsDockedPanelOpen(DockedPanel.ManageBatchReviewComments)
  const openManageBatchPanel = useOpenManageBatchPanel('for_you_pending_batch')
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const handleOpen = useCallback(() => {
    sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
      item_name: ITEM_NAME,
      action_name: 'manage_batch',
    })
    openManageBatchPanel({returnFocusTo: anchorRef.current})
  }, [openManageBatchPanel, sendPullRequestAnalyticsEvent])

  if (!unifiedBatchEnabled || batchCount === 0) return null

  const description = `${batchCount} unsubmitted item${batchCount === 1 ? '' : 's'} — only visible to you until submitted`

  return (
    <ForYouItem
      leadingVisual={<ForYouLeadingIcon icon={DiffIcon} variant="warning" />}
      title="You have a batch in progress"
      itemName={ITEM_NAME}
      description={description}
      actionSlot={
        <Button ref={anchorRef} variant="primary" size="small" onClick={handleOpen} aria-expanded={isPanelOpen}>
          Manage batch
        </Button>
      }
    />
  )
}
